'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var _extends = require('@babel/runtime/helpers/extends');
var React = require('react');
var clsx = require('clsx');
var nthHookTheme = require('@narvar/nth-hook-theme');
var nthHookTranslations = require('@narvar/nth-hook-translations');
var Option = require('./components/Option.js');
var Chevron = require('@narvar/nth-bit-icon-chevron');
var BlockSelect_module = require('./BlockSelect.module.css.js');
var reducer = require('./reducer.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var _extends__default = /*#__PURE__*/_interopDefaultLegacy(_extends);
var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var clsx__default = /*#__PURE__*/_interopDefaultLegacy(clsx);
var Chevron__default = /*#__PURE__*/_interopDefaultLegacy(Chevron);

const Select = _ref => {
  let {
    disabled,
    name,
    label,
    value,
    options,
    error,
    system,
    searchable,
    onChange
  } = _ref;
  const [state, dispatch] = React.useReducer(reducer.reducer, reducer.initialState);
  const {
    active,
    activeOption,
    inputActive,
    query,
    showMenu
  } = state;
  const filteredOptions = options.filter(option => option.label.toLowerCase().includes(query.trim().toLowerCase()));
  const hasError = error ? error.length > 0 : false;
  const isDisabled = !!disabled;
  const inputRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const {
    blockSelectDefaultLabel,
    blockSelectDefaultNoOptions
  } = nthHookTranslations.useTranslations();
  const displayLabel = label ?? blockSelectDefaultLabel ?? 'Select';
  const displayNoOptionLabel = blockSelectDefaultNoOptions ?? 'No options';
  const {
    bodytextDefaultNone: bodyText,
    inputtextinputsNoneDisabled: inputStyleDisabled,
    inputtextinputsNoneNone: inputStyle
  } = nthHookTheme.useTheme();
  const openListBox = React.useCallback(() => {
    var _inputRef$current;
    if (system) return;
    if (disabled) return;
    if (showMenu && searchable) return;
    dispatch({
      type: 'OPEN_LISTBOX_AND_ACTIVATE_QUERY_INPUT',
      payload: {
        inputActive: searchable ?? false
      }
    });
    if (value) {
      const optionIndex = filteredOptions.findIndex(option => option.value === value.value);
      dispatch({
        type: 'SET_ACTIVE_OPTION',
        payload: {
          activeOption: optionIndex
        }
      });
    }
    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
  }, [disabled, filteredOptions, searchable, value, showMenu, system]);
  const selectAndCloseListBox = React.useCallback(() => {
    var _inputRef$current2;
    const value = filteredOptions[activeOption] ?? null;
    dispatch({
      type: 'SELECT_ITEM_AND_CLOSE_LISTBOX'
    });
    if (onChange) onChange(value);
    (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 ? void 0 : _inputRef$current2.blur();
  }, [activeOption, filteredOptions, onChange]);

  // Todo: Make this custom hook for re-use
  React.useEffect(() => {
    const handleOutsideClick = event => {
      var _containerRef$current;
      const target = event.target;
      if (!((_containerRef$current = containerRef.current) !== null && _containerRef$current !== void 0 && _containerRef$current.contains(target))) {
        dispatch({
          type: 'OUTSIDE_CLICK'
        });
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [active, searchable]);
  React.useEffect(() => {
    const handleEnterKeyDown = () => {
      if (active) {
        if (showMenu) {
          selectAndCloseListBox();
        } else {
          openListBox();
        }
      }
    };
    const handleEscapeKeyDown = () => {
      var _inputRef$current3;
      dispatch({
        type: 'CLOST_LISTBOX_AND_UNSET_QUERY_INPUT'
      });
      (_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 ? void 0 : _inputRef$current3.blur();
    };
    const handleArrowUp = () => {
      if (filteredOptions.length <= 0) return;
      const optionIndex = activeOption <= 0 ? filteredOptions.length - 1 : activeOption - 1;
      dispatch({
        type: 'SET_ACTIVE_OPTION',
        payload: {
          activeOption: optionIndex
        }
      });
    };
    const handleArrowDown = () => {
      if (filteredOptions.length <= 0) return;
      if (!showMenu && active) {
        var _inputRef$current4;
        dispatch({
          type: 'SHOW_MENU_AND_ACTIVE_OPTION',
          payload: {
            inputActive: searchable ?? false
          }
        });
        if (value) {
          const optionIndex = filteredOptions.findIndex(option => option.value === value.value);
          dispatch({
            type: 'SET_ACTIVE_OPTION',
            payload: {
              activeOption: optionIndex
            }
          });
        }
        (_inputRef$current4 = inputRef.current) === null || _inputRef$current4 === void 0 ? void 0 : _inputRef$current4.focus();
        return;
      }
      const optionIndex = activeOption === filteredOptions.length - 1 ? 0 : activeOption + 1;
      dispatch({
        type: 'SET_ACTIVE_OPTION',
        payload: {
          activeOption: optionIndex
        }
      });
    };
    const handleSpaceKeyDown = event => {
      if (!active) return;

      // If the menu IS NOT open, but the list box is active, we open the list box.
      if (!showMenu) {
        event.preventDefault(); // prevents space from being captured when list box menu is in closed state.
        openListBox();
        return;
      }

      // If the menu IS open and NOT searchable, we select and close on spacebar (since the keyboard isn't supported for jump to).
      if (!searchable) {
        event.preventDefault(); // prevents space from being captured when list box menu doesn't accept entry.
        selectAndCloseListBox();
      }
    };
    const handleKeyDown = event => {
      if (system) return;
      switch (event.code) {
        case 'Enter':
          handleEnterKeyDown();
          break;
        case 'Escape':
          handleEscapeKeyDown();
          break;
        case 'ArrowUp':
          handleArrowUp();
          break;
        case 'ArrowDown':
          handleArrowDown();
          break;
        case 'Space':
          handleSpaceKeyDown(event);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [filteredOptions, activeOption, active, openListBox, system, searchable, value, showMenu, onChange, selectAndCloseListBox]);
  React.useEffect(() => {
    var _containerRef$current2;
    if (!showMenu) return;
    const option = (_containerRef$current2 = containerRef.current) === null || _containerRef$current2 === void 0 ? void 0 : _containerRef$current2.querySelector('li[aria-selected="true"]');
    option === null || option === void 0 ? void 0 : option.scrollIntoView({
      block: 'center'
    });
  }, [showMenu]);
  React.useEffect(() => {
    var _containerRef$current3;
    if (activeOption < 0) return;
    const option = (_containerRef$current3 = containerRef.current) === null || _containerRef$current3 === void 0 ? void 0 : _containerRef$current3.getElementsByTagName('li')[activeOption];
    option === null || option === void 0 ? void 0 : option.scrollIntoView({
      block: 'nearest'
    });
  }, [activeOption]);
  const handleFocus = () => {
    dispatch({
      type: 'SET_ACTIVE',
      payload: {
        active: true
      }
    });
  };
  const handleBlur = () => {
    dispatch({
      type: 'SET_ACTIVE',
      payload: {
        active: false
      }
    });
  };
  const handleInputChange = event => {
    const eventValue = event.currentTarget.value;
    dispatch({
      type: 'UPDATE_QUERY',
      payload: {
        query: eventValue
      }
    });
  };
  const handleOptionClick = option => {
    dispatch({
      type: 'OPTION_SELECTED'
    });
    if (onChange) onChange(option);
  };
  const handleDefaultChange = event => {
    const option = filteredOptions.find(option => option.value === event.currentTarget.value);
    const value = option ?? null;
    if (onChange) onChange(value);
  };
  const handleArrowClick = () => {
    if (!showMenu) return;
    if (!searchable) return;
    dispatch({
      type: 'CLOSE_SEARCHABLE_LISTBOX'
    });
  };
  const defaultAriaAttrs = {
    'aria-disabled': isDisabled
  };
  const ariaAttrs = system ? defaultAriaAttrs : {
    ...defaultAriaAttrs,
    'aria-label': displayLabel,
    'aria-expanded': showMenu,
    'aria-owns': `${name}-listbox`,
    'aria-haspopup': 'listbox'
  };
  const controlClasses = clsx__default["default"]({
    [BlockSelect_module["default"].selectControl]: true,
    [BlockSelect_module["default"].selectControlActive]: active,
    [BlockSelect_module["default"].selectControlAriaDisabled]: isDisabled,
    [BlockSelect_module["default"].selectControlError]: hasError
  });
  return /*#__PURE__*/React__default["default"].createElement("div", {
    className: BlockSelect_module["default"].selectContainer,
    ref: containerRef,
    style: bodyText
  }, /*#__PURE__*/React__default["default"].createElement("div", _extends__default["default"]({
    className: controlClasses,
    role: system ? 'presentation' : 'combobox',
    tabIndex: system ? -1 : 0,
    onClick: openListBox,
    onFocus: handleFocus,
    onBlur: handleBlur,
    style: isDisabled ? inputStyleDisabled : inputStyle
  }, ariaAttrs), system && /*#__PURE__*/React__default["default"].createElement("select", {
    className: BlockSelect_module["default"].defaultSelect,
    defaultValue: value === null || value === void 0 ? void 0 : value.value,
    disabled: isDisabled,
    placeholder: displayLabel,
    tabIndex: 0,
    name: name,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onChange: handleDefaultChange,
    "aria-label": name
  }, /*#__PURE__*/React__default["default"].createElement("option", null), options.map(option => /*#__PURE__*/React__default["default"].createElement("option", {
    key: option.value,
    value: option.value
  }, option.label))), /*#__PURE__*/React__default["default"].createElement("div", {
    className: clsx__default["default"]({
      [BlockSelect_module["default"].labelContainer]: true,
      [BlockSelect_module["default"].labelContainerFocused]: inputActive || !!value
    })
  }, /*#__PURE__*/React__default["default"].createElement("label", {
    className: BlockSelect_module["default"].label,
    htmlFor: name
  }, displayLabel)), /*#__PURE__*/React__default["default"].createElement("div", {
    className: BlockSelect_module["default"].inputContainer
  }, !inputActive && /*#__PURE__*/React__default["default"].createElement("div", {
    className: BlockSelect_module["default"].selected,
    "data-testid": "selectedValueLabel"
  }, value === null || value === void 0 ? void 0 : value.label), searchable && /*#__PURE__*/React__default["default"].createElement("input", {
    className: clsx__default["default"]({
      [BlockSelect_module["default"].input]: true,
      [BlockSelect_module["default"].inputActive]: inputActive,
      [BlockSelect_module["default"].disabledStyle]: isDisabled
    }),
    disabled: isDisabled,
    ref: inputRef,
    id: name,
    name: name,
    type: "text",
    value: query,
    autoComplete: "off",
    tabIndex: -1,
    "aria-autocomplete": "list",
    "aria-controls": showMenu ? `${name}-listbox` : undefined,
    onChange: handleInputChange,
    style: isDisabled ? inputStyleDisabled : inputStyle
  })), /*#__PURE__*/React__default["default"].createElement("div", {
    role: "button",
    className: BlockSelect_module["default"].selectArrow,
    onClick: handleArrowClick,
    tabIndex: 0
  }, /*#__PURE__*/React__default["default"].createElement(Chevron__default["default"], {
    direction: Chevron.Direction.Down,
    s: {
      color: 'currentColor',
      fill: 'currentColor',
      paddingBottom: '0.25rem',
      transform: `${showMenu ? `rotate(180deg)` : `none`}`,
      transformOrigin: 'center center',
      width: '0.875rem'
    }
  }))), showMenu && /*#__PURE__*/React__default["default"].createElement("div", {
    className: BlockSelect_module["default"].selectMenu,
    role: "presentation"
  }, /*#__PURE__*/React__default["default"].createElement("ul", {
    className: BlockSelect_module["default"].listBox,
    role: "listbox",
    id: `${name}-listbox`
  }, filteredOptions.length > 0 ? /*#__PURE__*/React__default["default"].createElement(React__default["default"].Fragment, null, filteredOptions.map((option, index) => /*#__PURE__*/React__default["default"].createElement(Option.Option, {
    key: option.value,
    selected: value ?? null,
    option: option,
    active: activeOption === index,
    onClick: handleOptionClick
  }))) : /*#__PURE__*/React__default["default"].createElement("div", {
    className: BlockSelect_module["default"].noOptions
  }, /*#__PURE__*/React__default["default"].createElement("span", null, displayNoOptionLabel)))));
};
var BlockSelect = nthHookTranslations.withTranslationsProvider(nthHookTheme.withThemeProvider(Select, {
  themeTokens: ['inputtextinputsNoneDisabled', 'inputtextinputsNoneNone', 'bodytextDefaultNone']
}), {
  translationKeys: ['blockSelectDefaultLabel', 'blockSelectDefaultNoOptions']
});

exports["default"] = BlockSelect;
